import { Dot, Star } from "lucide-react";
import { Server } from "@wajeez/wajeez.microservices";

import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { SVG } from "@components/items/common/SVG";
import { Card, CardContent, CardHeader, CardTitle } from "@ui/card";
import { Badge } from "@ui/badge";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { Skeleton } from "@ui/skeleton";
import { OnItemPress } from "@modules/content/hooks/useComponentData";
import { msToMinutesAndSeconds } from "@utils/helpers/timeFormtter";

export interface FeaturedContentParameters {
  shortDescription: string | null | undefined;
  image: string | null | undefined;
  backgroundImage: string | null | undefined;
  badgeTitle: string | null | undefined;
  badgeIcon: string | null | undefined;
  showAddToPlaylist: "False" | "True";
  readingTitle: string | null | undefined;
  listeningTitle: string | null | undefined;
  readingIcon: string | null | undefined;
  listeningIcon: string | null | undefined;
  titleBulletList: string | null | undefined;
  titleAuthorsList: string | null | undefined;
  subscriptionText: string | null | undefined;
  firstChapterFreeListeningTitle: string | null | undefined;
  subTitle: string | null | undefined;
  categories: string | null | undefined;
  coverImage: string | null | undefined;
}

interface FeaturedContentProps {
  parameterValues: Partial<FeaturedContentParameters>;
  content: Server.ContentEntityOutput | undefined | null;
  isLoading: boolean;
  onItemPress: OnItemPress<Partial<FeaturedContentParameters>>;
}

const FeaturedContent = ({
  parameterValues,
  content,
  isLoading,
  onItemPress,
}: FeaturedContentProps) => {
  const navigate = useAppNavigate();

  const {
    categories,
    image,
    coverImage,
    shortDescription,
    badgeTitle,
    readingIcon,
    readingTitle,
    listeningIcon,
    listeningTitle,
  } = parameterValues;

  const isLoadingVisible = useIsLoadingVisibile(isLoading && !content);

  return (
    <div
      className={`relative w-full ${
        (isLoadingVisible || isLoading) && "h-[400px]"
      } px-10`}
    >
      <div
        className={`absolute top-0 w-full px-10 ltr:left-0 rtl:right-0 ${
          isLoadingVisible && "z-10 bg-white dark:bg-black"
        }`}
      >
        <FeaturedContentLoading isLoading={isLoadingVisible} />
      </div>
      {content ? (
        <div className="pb-[35px]">
          <Card
            className="w-full cursor-pointer p-5"
            style={{
              backgroundImage: `url(${getPublicMediaUrl(coverImage)})`,
            }}
            onClick={() => {
              content &&
                onItemPress(content, parameterValues, { isLocked: false });
            }}
          >
            <div className="flex items-center justify-between">
              <CardHeader className="flex w-full flex-row items-center justify-between py-4">
                <CardTitle className="text-black dark:text-white">
                  <TranslatedText text="home.featuredCardTitle" />
                </CardTitle>
                {badgeTitle ? (
                  <Badge className="flex w-[93px] items-center justify-center gap-x-2 border border-black bg-transparent py-2 text-black dark:border-white dark:text-white">
                    <SVG src="/assets/icons/fremium.svg" />
                    <div>{badgeTitle}</div>
                  </Badge>
                ) : null}
              </CardHeader>
            </div>
            <CardContent className="flex items-center justify-start gap-x-8">
              <img
                src={getPublicMediaUrl(image)}
                alt="daily-content"
                width={250}
                height={250}
              />
              <div className="flex flex-col items-start justify-center text-black dark:text-white">
                <div>{content.title}</div>
                <div className="my-3">
                  <div>{content.authors?.join(", ")}</div>
                  <div className="gap-x flex items-center justify-start">
                    {categories}
                  </div>
                  <div className="gap-x mt-3 flex items-center justify-start">
                    <Badge className="gap-x-2 bg-[#858585] hover:bg-[#858585]">
                      <Star size={15} className="fill-white text-white" />
                      <div className="dark:text-white">
                        {content.rate?.toFixed(1)}
                      </div>
                    </Badge>
                    <Dot />
                    <div>{msToMinutesAndSeconds(content.durationMS || 0)}</div>
                  </div>
                </div>
                <div>{shortDescription}</div>
                <div className="mt-6 flex items-center justify-center gap-x-4">
                  {readingTitle ? (
                    <ShadcnButton className="border bg-transparent hover:bg-transparent hover:opacity-80 dark:border-white">
                      <div className="flex items-center justify-center gap-x-2 px-5 text-black dark:text-white">
                        <div className="h-6 w-6">
                          <SVG
                            src={getPublicMediaUrl(readingIcon)}
                            preProcessor={(code: string) =>
                              code
                                .replace(
                                  /(?<=fill=)"(?!none")[^"]+/g,
                                  `"currentcolor"`
                                )
                                .replace(
                                  /(?<=stroke=)"(?!none")[^"]+/g,
                                  `"currentcolor"`
                                )
                            }
                          />
                        </div>
                        <div>{readingTitle}</div>
                      </div>
                    </ShadcnButton>
                  ) : null}
                  <ShadcnButton className="bg-primary hover:bg-primary/80">
                    <div className="flex items-center justify-center gap-x-2 px-5 text-white">
                      <div className="h-4 w-4">
                        <SVG
                          src={getPublicMediaUrl(listeningIcon)}
                          preProcessor={(code: string) =>
                            code
                              .replace(
                                /(?<=fill=)"(?!none")[^"]+/g,
                                `"currentcolor"`
                              )
                              .replace(
                                /(?<=stroke=)"(?!none")[^"]+/g,
                                `"currentcolor"`
                              )
                          }
                        />
                      </div>
                      <div>{listeningTitle}</div>
                    </div>
                  </ShadcnButton>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </div>
  );
};

const FeaturedContentLoading = ({ isLoading }: { isLoading: boolean }) => {
  return isLoading ? (
    <Skeleton
      className={`h-[375px] w-full rounded-[12px] bg-[#f4f4f5] pb-[35px] dark:bg-[#27272a]`}
    ></Skeleton>
  ) : null;
};

export default FeaturedContent;
